<template>
    <label
        ref="rootElement"
        :for="props.for"
        :class="
        {
            'Input-label'        : true,

            [props.type]         : props.type,

            'icon'               : props.icon,
            [props.iconPosition] : props.iconPosition,

            'success'            : isStatusVisible && props.successText,
            'warning'            : isStatusVisible && props.warningText,
            'error'              : isStatusVisible && props.errorText,
        }"
        @click="$_helpers.handleFocus"
    >
        <div v-if="props.title" class="Input-label-title" :class="{ 'underline': props.tooltipType === 'underline' }">
            <VTooltip popperClass="Info-tooltip-wrap" :placement="'top'" :triggers="['hover']" :distance="12" :disabled="!props.tooltip">
                <span>
                    <span>{{props.title}}</span>
                    <span class="Input-label-required" v-if="props.required">*</span>
                    <span class="Input-label-optional" v-if="props.optional"> (optional)</span>
                    <span class="Input-label-tooltip" v-if="props.tooltip && props.tooltipType === 'icon'"> <i class="isax isax-info-circle"></i></span>
                </span>

                <template #popper>
                    <div v-if="$slots.tooltipTitle" class="tooltip-title">
                        <slot name="tooltipTitle"></slot>
                    </div>
                    <div class="tooltip-description">
                        <slot name="tooltipDescription">
                            <div v-html="props.tooltip"></div>
                        </slot>
                    </div>
                </template>
            </VTooltip>
        </div>

        <i class="Input-label-icon isax" :class="[icon, iconPosition]" v-if="icon"></i>
        <slot></slot>
        <Transition>
            <span class="Input-label-status" v-if="isStatusVisible">
                {{props.successText || props.warningText || props.errorText}}
            </span>
        </Transition>
    </label>
</template>

<script setup>
import { ref, watch } from "vue";

const props = defineProps(
{
    for         : { type: String },
    title       : { type: String },
    type        : { type: String, default: 'input', validator( value ) { return [ 'input', 'select', 'file', 'date', 'checkbox', 'radio', 'toggle' ].includes( value ) } },
    icon        : { type: String },
    iconPosition: { type: String, validator( value ) { return [ 'left', 'right' ].includes( value ) } },
    successText : { type: String },
    warningText : { type: String },
    errorText   : { type: String },
    required    : { type: Boolean, default: false },
    tooltip     : { type: [ Boolean, String ] },
    tooltipType : { type: String, default: 'icon', validator( value ) { return [ 'icon', 'underline' ].includes( value ) } },
    optional    : { type: Boolean, default: false }
});

const rootElement = ref( null );

const isStatusVisible = ref(!!( props.successText || props.warningText || props.errorText ));

const $_helpers =
{
    handleFocus: ( event ) =>
    {
        if( !event.target.classList.contains('Input-label') && !event.target.classList.contains('Input-label-title') )
        {
            return false;
        }

        if( props.type === 'select' )
        {
            //TODO
        }
    },

    handleStatusChange: ( text ) =>
    {
        if( text?.trim()?.length )
        {
            isStatusVisible.value = true;
        }
        else if( !props.disabled )
        {
            isStatusVisible.value = false;
        }
    }
}

watch( () => props.successText, $_helpers.handleStatusChange );
watch( () => props.warningText, $_helpers.handleStatusChange );
watch( () => props.errorText,   $_helpers.handleStatusChange );
</script>

<style lang="scss">
.Input-label
{
    --input-icon-offset  : 0;
    --input-x-padding    : 0;
    --input-y-padding    : 0;
    --input-opacity      : 1;
    --input-status-color : var(--light-surfaces-oslo);

    position: relative;

    display: grid;
    grid-template-rows: auto 1fr auto;

    width: 100%;

    padding: 0;
    margin: auto;

    &.secondary
    {
        .Input-label-title
        {
            width: max-content;

            margin-bottom: calc( ( var(--line-height) / 2 ) * -1 );
            margin-left: calc( var(--input-y-padding) - #{ px(4) } + 1px);

            padding: 0 px(4);

            background-color: var(--input-background-color);

            border-radius: 2px;

            z-index: 2;

        }

        &:has( .Input-label-title )
        {
            --input-icon-offset: calc( ( var(--line-height) / 2 ) );
        }
    }

    .v-enter-active,
    .v-leave-active
    {
        transition: all 250ms ease-in-out;
    }

    .v-enter-from,
    .v-leave-to
    {
        opacity: 0;
        transform: translateY( #{px(-2)} );
    }

    &:not(.secondary):has( .Input-label-title )
    {
        --input-icon-offset: calc( #{fpx(20)} + #{px(4)} );
    }

    &:has( input.lg ),
    &:has( .Select.lg ),
    &:has( .Treeselect.lg ),
    &:has( .Datepicker.lg )
    {
        --input-x-padding: var(--input-lg-x-padding);
        --input-y-padding: var(--input-lg-y-padding);

        &.secondary
        {
            .Input-label-title,
            .Input-label-icon
            {
                @include font-caption-md-regular;
            }
        }
    }

    &:has( input.md ),
    &:has( .Select.md ),
    &:has( .Treeselect.md ),
    &:has( .Datepicker.md )
    {
        --input-x-padding: var(--input-md-x-padding);
        --input-y-padding: var(--input-md-y-padding);

        &.secondary
        {
            .Input-label-title,
            .Input-label-icon
            {
                @include font-caption-md-regular;
            }
        }

    }

    &:has( input.sm ),
    &:has( .Select.sm ),
    &:has( .Treeselect.sm ),
    &:has( .Datepicker.sm )
    {
        --input-x-padding: var(--input-sm-x-padding);
        --input-y-padding: var(--input-sm-y-padding);

        &.secondary
        {
            .Input-label-title,
            .Input-label-icon
            {
                @include font-caption-sm-regular;
            }
        }
    }

    &.success,
    &:has( input.success ),
    &:has( .Select.success ),
    &:has( .Treeselect.success ),
    &:has( .Datepicker.success )
    {
        --input-status-color: var(--input-success-text-color);

        input[type='tel'], input[type='text']:not(.vue-treeselect__input), input[type='email'], input[type='url'], input[type='password'], input[type='number'], input[type='search']:not(.Select-input-search),

        textarea,

        .Select,

        .Datepicker
        {
            color: var(--input-success-text-color);
            background: var(--input-success-background-color);

            border-style: var(--input-success-border-style);
            border-width: var(--input-success-border-width);
            border-color: var(--input-success-border-color);
        }
    }

    &.warning,
    &:has( input.warning ),
    &:has( .Select.warning ),
    &:has( .Treeselect.warning ),
    &:has( .Datepicker.warning )
    {
        --input-status-color: var(--input-warning-text-color);

        input[type='tel'], input[type='text']:not(.vue-treeselect__input), input[type='email'], input[type='url'], input[type='password'], input[type='number'], input[type='search']:not(.Select-input-search),

        textarea,

        .Select,

        .Datepicker
        {
            color: var(--input-warning-text-color);
            background: var(--input-warning-background-color);

            border-style: var(--input-warning-border-style);
            border-width: var(--input-warning-border-width);
            border-color: var(--input-warning-border-color);
        }
    }

    &.error,
    &:has( input.error ),
    &:has( .Select.error ),
    &:has( .Treeselect.error ),
    &:has( .Datepicker.error )
    {
        --input-status-color: var(--input-error-text-color);

        input[type='tel'], input[type='text']:not(.vue-treeselect__input), input[type='email'], input[type='url'], input[type='password'], input[type='number'], input[type='search']:not(.Select-input-search),

        textarea,

        .Select,

        .Datepicker
        {
            color: var(--input-error-text-color);
            background: var(--input-error-background-color);

            border-style: var(--input-error-border-style);
            border-width: var(--input-error-border-width);
            border-color: var(--input-error-border-color);
        }
    }

    &:has( input[ disabled ] ),
    &:has( .Select.vs--disabled ),
    &:has( .Treeselect.vue-treeselect--disabled ),
    &:has( .Datepicker.disabled ),
    {
        --input-opacity: 0.5;
    }

    &.icon
    {
        &.left
        {
            input:not(.Select-input-search):not(.vue-treeselect__input)
            {
                &[type='tel'],
                &[type='text'],
                &[type='email'],
                &[type='url'],
                &[type='password'],
                &[type='number'],
                &[type='search']
                {
                    padding-left: calc( var(--input-x-padding) + #{px(24)} + #{px(8)} );
                }
            }

            .Select > .Select-input,
            .Treeselect > .vue-treeselect__control,
            .Datepicker > .Datepicker-input
            {
                padding-left: calc( var(--input-x-padding) + #{px(24)} + #{px(8)} );
            }
        }

        &.right
        {
            input:not(.Select-input-search):not(.vue-treeselect__input)
            {
                &[type='tel'],
                &[type='text'],
                &[type='email'],
                &[type='url'],
                &[type='password'],
                &[type='number'],
                &[type='search']
                {
                    padding-right: calc( var(--input-x-padding) + #{px(24)} + #{px(8)} );
                }
            }

            .Select > .Select-input,
            .Treeselect > .vue-treeselect__control,
            .Datepicker > .Datepicker-input
            {
                padding-right: calc( var(--input-x-padding) + #{px(24)} + #{px(8)} );
            }
        }
    }

    &.checkbox,
    &.radio,
    &.toggle
    {
        align-items: center;
        justify-content: flex-start;

        grid-template-rows: 1fr;
        grid-template-columns: auto 1fr;
        grid-template-areas: "input label" "status status";

        grid-column-gap: px(8);

        cursor: pointer;

        > input
        {
            grid-area: input;
        }

        > .Input-label-title
        {
            grid-area: label;

            padding: 0;
        }

        > span.Input-label-status
        {
            grid-area: status;
        }
    }

    > .Input-label-title
    {
        display: flex;
        flex-direction: row;

        font-size: fpx(14);
        line-height: fpx(20);
        font-weight: 400;
        font-style: normal;

        gap: px(4);

        color: var(--secondary-text-color);

        padding-bottom: px(4);

        opacity: var(--input-opacity);

        user-select: none;

        &.underline > div > span > span
        {
            text-decoration: underline;
            text-decoration-style: dashed;

            cursor: help;
        }

        > div
        {
            > span
            {
                display: flex;
                flex-direction: row;

                gap: px(4);

                > span.Input-label-required
                {
                    font-weight: bold;

                    color: var(--error-text-color);
                }

                > span.Input-label-optional
                {
                    color: var(--disabled-text-color);
                }

                > span.Input-label-tooltip
                {
                    i
                    {
                        display: flex;
                        align-items: center;
                        justify-content: center;

                        font-size: fpx(20);
                        line-height: fpx(20);

                        color: var(--light-surfaces-gainsboro);

                        transform: rotate(180deg);

                        cursor: help;

                        &:hover
                        {
                            color: var(--light-surfaces-silver);
                        }
                    }
                }
            }
        }
    }

    > i.Input-label-icon
    {
        position: absolute;

        top: calc( 50% + ( var(--input-icon-offset) / 2 ) );

        transform: translateY( -50% );

        display: flex;
        align-items: center;
        justify-content: center;

        width: px(24);
        height: px(24);
        font-size: px(24)!important;
        line-height: px(24)!important;

        color: var(--secondary-text-color);
        opacity: var(--input-opacity);

        z-index: 1;

        &.left
        {
            left: var(--input-x-padding);
        }

        &.right
        {
            right: var(--input-x-padding);
        }
    }

    > span.Input-label-status
    {
        @include font-caption-md-regular;

        position: absolute;

        top: 100%;
        left: 0;

        padding-top: px(4);

        color: var(--input-status-color);

        opacity: var(--input-opacity);

        user-select: none;
    }
}
</style>